import type { AxiosHeaders, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios, { AxiosError } from 'axios';
import { toast } from 'sonner';

import { auth0Functions } from '~/shared/lib/auth-0';
import { useAuthStore } from '~/shared/model/auth';

import { generalConfig } from '../config/general';

const AXIOS_BASE: string = generalConfig.VITE_API_SERVER_URL;
declare const AXIOS_HEADERS: string;

export type RequestConfig<TData = unknown> = {
  url?: string;
  method: 'get' | 'put' | 'patch' | 'post' | 'delete';
  params?: unknown;
  data?: TData;
  responseType?:
    | 'arraybuffer'
    | 'blob'
    | 'document'
    | 'json'
    | 'text'
    | 'stream';
  signal?: AbortSignal;
  headers?: AxiosRequestConfig['headers'];
};

export type ResponseConfig<TData = unknown> = {
  data: TData;
  status: number;
  statusText: string;
  headers?: AxiosResponse['headers'];
};

export const axiosInstance = axios.create({
  // eslint-disable-next-line unicorn/no-typeof-undefined
  baseURL: typeof AXIOS_BASE === 'undefined' ? undefined : AXIOS_BASE,
  headers:
    // eslint-disable-next-line unicorn/no-typeof-undefined
    typeof AXIOS_HEADERS === 'undefined'
      ? undefined
      : (JSON.parse(AXIOS_HEADERS) as AxiosHeaders),
});

axiosInstance.interceptors.request.use((config) => {
  config.headers.setAuthorization(
    `Bearer ${useAuthStore.getState().accessToken}`,
  );
  return config;
});

async function logout() {
  const state = useAuthStore.getState();
  const { isAuthorized } = state;
  state.logout();
  if (auth0Functions.logout) {
    await auth0Functions.logout();
  }
  if (isAuthorized) {
    toast.error('Authentication expired.');
  }
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error instanceof AxiosError && error.response?.status === 401) {
      const originalRequest = error.config;
      if (
        originalRequest &&
        !(originalRequest as unknown as { retry: boolean }).retry &&
        auth0Functions.getAccessTokenSilently
      ) {
        try {
          (originalRequest as unknown as { retry: boolean }).retry = true;
          const accessToken = await auth0Functions.getAccessTokenSilently();
          useAuthStore.setState({
            isAuthorized: true,
            accessToken,
          });
          const originalResponse = await axiosInstance.request(originalRequest);
          return originalResponse;
        } catch (error_) {
          await logout();
          throw error_;
        }
      }
      await logout();
      throw error;
    } else {
      throw error;
    }
  },
);

export const client = async <TData, TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> => {
  const promise = axiosInstance
    .request<TVariables, ResponseConfig<TData>>({ ...config })
    .catch((error: AxiosError<TError>) => {
      throw error;
    });

  return promise;
};

export default client;
